import React, { useState, useRef, useEffect, useMemo } from "react";
import { useCurrency } from "../contexts/CurrencyContext";

/**
 * Header dropdown that lets customers pick a display currency. All prices
 * across the site read from `useCurrency()` — this is a pure UI toggle.
 *
 * The picker keeps AED pinned at the top (it's the platform base and where
 * all transactions actually settle), followed by an alphabetical list of
 * every currency we have both a live rate AND metadata (flag + symbol)
 * for. A search box surfaces the ~50 pre-configured currencies without
 * making users scroll — most will already know their 3-letter code.
 */
export default function CurrencySwitcher({ compact = false }) {
  const { currency, setCurrency, availableCurrencies, meta, source, fetchedAt } = useCurrency();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Auto-focus the search box whenever the dropdown opens.
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 20);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return availableCurrencies;
    return availableCurrencies.filter(
      (c) =>
        c.code.toLowerCase().includes(q) ||
        (c.name || "").toLowerCase().includes(q)
    );
  }, [availableCurrencies, query]);

  const pick = (code) => {
    setCurrency(code);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="currency-switcher-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        title={`Displaying prices in ${meta.name || currency}. Backend charges are always in AED.`}
        className={`inline-flex items-center gap-1.5 uppercase tracking-widest text-xs text-white/70 hover:text-gold transition-colors ${
          compact ? "px-2 py-1" : "px-3 py-1.5"
        } border border-white/15 hover:border-gold/60 rounded`}
      >
        <span aria-hidden="true" className="text-base leading-none">{meta.flag || "🌐"}</span>
        <span className="tabular-nums">{currency}</span>
        <svg className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-72 z-50 glass rounded-lg shadow-2xl border border-white/10 overflow-hidden"
          data-testid="currency-switcher-panel"
        >
          <div className="p-2 border-b border-white/10">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search currency…"
              data-testid="currency-switcher-search"
              className="w-full bg-transparent text-sm px-2 py-1.5 border border-white/15 focus:border-gold rounded outline-none"
            />
          </div>
          <ul role="listbox" className="max-h-72 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-xs text-white/40">No match</li>
            )}
            {filtered.map((c) => {
              const active = c.code === currency;
              return (
                <li key={c.code}>
                  <button
                    type="button"
                    onClick={() => pick(c.code)}
                    data-testid={`currency-option-${c.code}`}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left transition-colors ${
                      active ? "bg-gold/10 text-gold" : "text-white/80 hover:bg-white/[0.06]"
                    }`}
                  >
                    <span aria-hidden="true" className="text-lg leading-none w-6">{c.flag || "🌐"}</span>
                    <span className="tabular-nums font-medium w-12">{c.code}</span>
                    <span className="text-xs text-white/50 truncate flex-1">{c.name}</span>
                    {active && (
                      <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="border-t border-white/10 px-3 py-2 text-[10px] text-white/40 leading-snug">
            Display only — all payments settle in AED.
            {source && source !== "initial" && fetchedAt && (
              <span className="block mt-0.5">
                Rates via {source === "open.er-api.com" ? "open.er-api.com" : source} · updated {new Date(fetchedAt).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
